import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, Sparkles, AlertCircle, Briefcase } from "lucide-react";
import { Reveal } from "../Reveal";
import { TECH_STACK, SERVICE_CATEGORIES } from "../../data/site";

const logoFor = (name) => {
  const t = TECH_STACK.find((x) => x.name === name);
  if (!t) return null;
  return t.src || `https://cdn.simpleicons.org/${t.slug}`;
};

const List = ({ label, items, Icon, testid }) => (
  <div data-testid={testid}>
    <p className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-4">{label}</p>
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-slate-200 text-sm">
          <Icon size={14} className="mt-0.5 text-brand-orange shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export const ServiceSection = ({ cat, detail, index }) => {
  const Icon = cat.icon;
  const flip = index % 2 === 1;
  const n = String(index + 1).padStart(2, "0");
  const total = String(SERVICE_CATEGORIES.length).padStart(2, "0");
  const relatedCat = detail.related && SERVICE_CATEGORIES.find((c) => c.id === detail.related.id);

  return (
    <section
      id={cat.id}
      data-testid={`service-section-${cat.id}`}
      className={`relative py-20 md:py-28 scroll-mt-28 ${flip ? "bg-navy" : "bg-navy-deep"}`}
    >
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
            <Reveal>
              <span className="font-mono text-xs text-brand-orange tracking-widest">{n} / {total}</span>
              <div className="w-16 h-16 rounded-2xl bg-brand-orange/15 grid place-items-center mt-5">
                <Icon size={30} className="text-brand-orange" />
              </div>
              <h2 className="font-head text-3xl md:text-4xl font-semibold text-white mt-6 tracking-tight">{cat.title}</h2>
              <p className="text-brand-orange font-medium mt-3">{detail.tagline}</p>
              <p className="text-slate-300 leading-[1.8] mt-5">{detail.overview}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8">
                <p className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-3">Business challenges we solve</p>
                <ul className="space-y-2.5">
                  {detail.challenges.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-slate-300 text-sm">
                      <AlertCircle size={14} className="mt-0.5 text-brand-orange shrink-0" /> {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-8">
                <p className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-3">Ideal for</p>
                <div className="flex flex-wrap gap-2">
                  {detail.idealFor.map((i) => (
                    <span key={i} className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-slate-200">{i}</span>
                  ))}
                </div>
              </div>
              <Link to="/contact" data-testid={`service-cta-${cat.id}`} className="btn-primary mt-8">
                Talk to an Expert <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden card-glow"
            >
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-brand-orange/10 blur-3xl" />
              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8">
                <List label="Sub-services" items={cat.items} Icon={Check} testid={`service-items-${cat.id}`} />
                <div className="space-y-8">
                  <List label="Business benefits" items={detail.outcomes} Icon={Sparkles} testid={`service-outcomes-${cat.id}`} />
                  <List label="Typical use cases" items={detail.useCases} Icon={Briefcase} testid={`service-usecases-${cat.id}`} />
                </div>
              </div>

              <div className="relative mt-8 pt-8 border-t border-white/10">
                <p className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-4">Technologies &amp; platforms</p>
                <div className="flex flex-wrap gap-2.5">
                  {detail.tools.map((t) => {
                    const src = logoFor(t);
                    return (
                      <span key={t} data-testid={`service-tool-${cat.id}-${t.toLowerCase().replace(/[^a-z0-9]/g, "")}`} className="inline-flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 text-xs font-medium text-[#0A1F44]">
                        {src && <img src={src} alt="" className="w-4 h-4 object-contain" loading="lazy" />}
                        {t}
                      </span>
                    );
                  })}
                </div>
              </div>

              {relatedCat && (
                <Link
                  to={`/services#${relatedCat.id}`}
                  data-testid={`service-related-${cat.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(relatedCat.id);
                    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100 });
                    else el?.scrollIntoView({ behavior: "smooth" });
                    window.history.replaceState(null, "", `/services#${relatedCat.id}`);
                  }}
                  className="relative mt-8 flex items-start gap-3 rounded-2xl border border-brand-orange/30 bg-brand-orange/10 px-5 py-4 text-sm text-slate-200 hover:bg-brand-orange/15 transition-colors"
                >
                  <ArrowRight size={16} className="mt-0.5 text-brand-orange shrink-0" />
                  <span>{detail.related.text}</span>
                </Link>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceSection;
